import type { AppState } from '../../state/appState.js';
import { notifyEditorTargetSynced } from '../editorEvents.js';
import type {
  GeometrySlot,
  MaterialSlot,
} from '../../scene/layerState.js';
import type { SetComponentSlotAction } from './componentSlot.js';
import {
  cloneSlotValue,
  currentInlineSlotValueFor,
} from './shared.js';

export interface AutoDetachComponentSlotHooks {
  notifyEditorTargetSynced?: () => void;
}

export const editorAutoDetachComponentSlotHooks: AutoDetachComponentSlotHooks = {
  notifyEditorTargetSynced,
};

export function autoDetachEditedComponentSlotIfNeededFor(
  appState: AppState,
  hooks: AutoDetachComponentSlotHooks = {},
): { replayAction: SetComponentSlotAction; undoAction: SetComponentSlotAction } | null {
  const context = appState.editorContext;
  if (context.mode !== 'layer' || !context.layerId || !context.layerSlot) return null;
  const componentId = context.componentId ?? null;

  const layerDef = appState.layerLibrary[context.layerId];
  if (!layerDef) return null;

  const currentSlot = context.layerSlot === 'geometry' ? layerDef.geometry : layerDef.material;
  if (currentSlot.type !== 'library') return null;

  const nextValue = currentInlineSlotValueFor(appState, context.layerSlot);
  if (context.layerSlot === 'geometry') layerDef.geometry = nextValue as GeometrySlot;
  else layerDef.material = nextValue as MaterialSlot;
  appState.editorGraphDirty = false;
  hooks.notifyEditorTargetSynced?.();

  return {
    replayAction: {
      type: 'component:setSlot',
      componentId,
      layerId: context.layerId,
      slot: context.layerSlot,
      value: cloneSlotValue(nextValue),
    },
    undoAction: {
      type: 'component:setSlot',
      componentId,
      layerId: context.layerId,
      slot: context.layerSlot,
      value: { type: 'library', id: currentSlot.id },
    },
  };
}
